import { Fragment, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Tab, Dialog, Transition } from '@headlessui/react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import {
  fetchUser,
  notifyUser,
  grantTrialExtension,
  impersonateUser,
} from '@/lib/admin/users';
import type { ChurnRisk } from '@/types/admin';
import { useAuthStore } from '@/stores/authStore';
import PageSkeleton from '@/components/PageSkeleton';
import { cn } from '@/lib/cn';
import {
  BellIcon,
  GiftIcon,
  UserCircleIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';

function ChurnBadge({ risk }: { risk: ChurnRisk }) {
  const { t } = useTranslation();
  const styles: Record<ChurnRisk, string> = {
    LOW: 'bg-emerald-100 text-emerald-700',
    MEDIUM: 'bg-amber-100 text-amber-700',
    HIGH: 'bg-rose-100 text-rose-700',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold uppercase', styles[risk])}>
      {t(`admin.churnRisk.${risk.toLowerCase()}`)}
    </span>
  );
}

function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
              <Dialog.Title className="text-base font-semibold text-gray-900">{title}</Dialog.Title>
              <div className="mt-4">{children}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default function UserDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const impersonate = useAuthStore((s) => s.impersonate);

  const { data: user, isLoading, isError } = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => fetchUser(id),
    enabled: !!id,
  });

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [impConfirmOpen, setImpConfirmOpen] = useState(false);

  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyChannel, setNotifyChannel] = useState<'IN_APP' | 'EMAIL' | 'BOTH'>('IN_APP');
  const [trialDays, setTrialDays] = useState(7);

  const notifyMut = useMutation({
    mutationFn: () =>
      notifyUser(id, { title: notifyTitle, message: notifyMessage, channel: notifyChannel }),
    onSuccess: () => {
      toast.success(t('admin.toasts.notificationSent'));
      setNotifyOpen(false);
      setNotifyTitle('');
      setNotifyMessage('');
    },
    onError: () => toast.error(t('common.error')),
  });

  const trialMut = useMutation({
    mutationFn: () => grantTrialExtension(id, trialDays),
    onSuccess: () => {
      toast.success(t('admin.toasts.trialGranted'));
      setTrialOpen(false);
      qc.invalidateQueries({ queryKey: ['admin', 'user', id] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const impMut = useMutation({
    mutationFn: () => impersonateUser(id),
    onSuccess: (resp) => {
      impersonate(resp.token, resp.user);
      toast.success(t('admin.toasts.impersonating'));
      setImpConfirmOpen(false);
      navigate('/');
    },
    onError: () => toast.error(t('common.error')),
  });

  if (isLoading) return <PageSkeleton variant="detail" />;
  if (isError || !user) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        {t('common.error')}
      </div>
    );
  }

  const memberships = user.memberships ?? [];
  const subscriptions = user.subscriptions ?? [];
  const recentActivities = user.recentActivities ?? [];
  const sessions = user.sessions ?? [];
  const intents30d = user.intents30d ?? [];
  const ticketCount = user.ticketCount ?? 0;
  const errors30d = user.errors30d ?? 0;

  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const fullName = `${user.firstName} ${user.lastName}`.trim() || user.email;

  return (
    <div className="space-y-6">
      <Link to="/admin/users" className="text-sm text-primary-700 hover:underline">
        ← {t('admin.nav.users')}
      </Link>

      {/* Header */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xl font-bold text-primary-700">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{fullName}</h1>
              <ChurnBadge risk={user.churnRisk} />
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold uppercase',
                  user.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600',
                )}
              >
                {user.isActive ? t('admin.userStatus.active') : t('admin.userStatus.inactive')}
              </span>
            </div>
            <p className="text-sm text-gray-500">{user.email}</p>
            <p className="mt-1 text-xs text-gray-400">
              {t('admin.engagement')}: {user.engagementScore}
            </p>

            {user.suggestedAction && (
              <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                <span className="font-semibold">{t('admin.suggestedAction')}:</span>{' '}
                {t(user.suggestedAction)}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setNotifyOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-white border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <BellIcon className="h-4 w-4" />
              {t('admin.actions.notify')}
            </button>
            <button
              type="button"
              onClick={() => setTrialOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-white border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <GiftIcon className="h-4 w-4" />
              {t('admin.actions.grantTrial')}
            </button>
            <button
              type="button"
              onClick={() => setImpConfirmOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600"
            >
              <ArrowsRightLeftIcon className="h-4 w-4" />
              {t('admin.actions.impersonate')}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tab.Group>
        <Tab.List className="flex flex-wrap gap-1 border-b border-gray-200">
          {['profile', 'activity', 'sessions', 'tickets', 'errors', 'intents'].map((key) => (
            <Tab
              key={key}
              className={({ selected }) =>
                cn(
                  'rounded-t-lg px-4 py-2 text-sm font-medium focus:outline-none',
                  selected
                    ? 'border-b-2 border-primary-600 text-primary-700'
                    : 'text-gray-600 hover:text-gray-900',
                )
              }
            >
              {t(`admin.tabs.${key}`)}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-4">
          {/* Profile */}
          <Tab.Panel>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="mb-2 text-sm font-semibold text-gray-700">{t('admin.memberships')}</h3>
                {memberships.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('common.noResults')}</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {memberships.map((m) => (
                      <li key={m.id} className="py-2 text-sm">
                        <span className="font-medium">{m.organization.name}</span>
                        <span className="ml-2 text-xs text-gray-500">{m.role}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="mb-2 text-sm font-semibold text-gray-700">
                  {t('admin.nav.subscriptions')}
                </h3>
                {subscriptions.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('common.noResults')}</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-gray-500">
                      <tr>
                        <th className="py-1">{t('admin.columns.plan')}</th>
                        <th className="py-1">{t('admin.columns.subStatus')}</th>
                        <th className="py-1">{t('admin.columns.trialEnd')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((s, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="py-2 capitalize">{s.plan}</td>
                          <td className="py-2 capitalize">{s.status}</td>
                          <td className="py-2 text-xs text-gray-600">
                            {s.trialEndsAt ? new Date(s.trialEndsAt).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </Tab.Panel>

          {/* Activity */}
          <Tab.Panel>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              {recentActivities.length === 0 ? (
                <p className="text-sm text-gray-500">{t('common.noResults')}</p>
              ) : (
                <ol className="space-y-3">
                  {recentActivities.map((a) => (
                    <li key={a.id} className="flex items-start gap-3">
                      <UserCircleIcon className="h-5 w-5 shrink-0 text-gray-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{a.type}</div>
                        {a.description && (
                          <div className="text-xs text-gray-600">{a.description}</div>
                        )}
                        <div className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </Tab.Panel>

          {/* Sessions */}
          <Tab.Panel>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2">{t('admin.columns.ip')}</th>
                    <th className="px-4 py-2">{t('admin.columns.userAgent')}</th>
                    <th className="px-4 py-2">{t('admin.columns.createdAt')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sessions.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-gray-500">
                        {t('common.noResults')}
                      </td>
                    </tr>
                  )}
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td className="px-4 py-2">{s.ipAddress ?? '—'}</td>
                      <td className="truncate px-4 py-2">{s.userAgent ?? '—'}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {new Date(s.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tab.Panel>

          {/* Tickets */}
          <Tab.Panel>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-700">
                {t('admin.tickets.totalCount', { count: ticketCount })}
              </p>
              <Link to={`/admin/tickets?q=${encodeURIComponent(user.email)}`} className="text-sm text-primary-700 hover:underline">
                {t('admin.tickets.viewAll')} →
              </Link>
            </div>
          </Tab.Panel>

          {/* Errors */}
          <Tab.Panel>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-700">
                {t('admin.errors.last30d', { count: errors30d })}
              </p>
            </div>
          </Tab.Panel>

          {/* Intents */}
          <Tab.Panel>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              {intents30d.length === 0 ? (
                <p className="text-sm text-gray-500">{t('common.noResults')}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {intents30d.map((it) => (
                    <span
                      key={it.type}
                      className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700"
                    >
                      {t(`intents.${it.type}`, { defaultValue: it.type })}
                      <span className="ml-1 text-indigo-500">×{it.count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Notify Modal */}
      <Modal open={notifyOpen} onClose={() => setNotifyOpen(false)} title={t('admin.actions.notify')}>
        <div className="space-y-3">
          <input
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            placeholder={t('admin.notify.title')}
            value={notifyTitle}
            onChange={(e) => setNotifyTitle(e.target.value)}
          />
          <textarea
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            rows={4}
            placeholder={t('admin.notify.message')}
            value={notifyMessage}
            onChange={(e) => setNotifyMessage(e.target.value)}
          />
          <select
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={notifyChannel}
            onChange={(e) => setNotifyChannel(e.target.value as 'IN_APP' | 'EMAIL' | 'BOTH')}
          >
            <option value="IN_APP">{t('admin.notify.channels.inApp')}</option>
            <option value="EMAIL">{t('admin.notify.channels.email')}</option>
            <option value="BOTH">{t('admin.notify.channels.both')}</option>
          </select>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setNotifyOpen(false)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={notifyMut.isPending || !notifyTitle.trim() || !notifyMessage.trim()}
              onClick={() => notifyMut.mutate()}
              className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Trial Modal */}
      <Modal open={trialOpen} onClose={() => setTrialOpen(false)} title={t('admin.actions.grantTrial')}>
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">{t('admin.trial.days')}</label>
          <input
            type="number"
            min={1}
            max={90}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={trialDays}
            onChange={(e) => setTrialDays(Number(e.target.value))}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setTrialOpen(false)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={trialMut.isPending}
              onClick={() => trialMut.mutate()}
              className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Impersonate confirm */}
      <Modal
        open={impConfirmOpen}
        onClose={() => setImpConfirmOpen(false)}
        title={t('admin.actions.impersonate')}
      >
        <p className="text-sm text-gray-700">{t('admin.impersonateConfirm', { name: fullName })}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setImpConfirmOpen(false)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={impMut.isPending}
            onClick={() => impMut.mutate()}
            className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {t('common.confirm')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
